import {
  useState,
  useEffect,
  ReactNode,
  createContext,
  useMemo,
  useContext,
} from 'react';
import { AlmaAccountCore, User } from '@almamedia/alma-account-core';
import { getAccountInformation } from '../../api/api';
import { addEventToGTMDataLayer } from '../../utilities/gtm';
import {
  IIdentityContext,
  TujuAccountInformation,
  AlmaAccountActions,
} from '@almamedia-legal-insights/types';

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<IIdentityContext>({
  actions: undefined,
  user: undefined,
  loading: true,
  authInfo: undefined,
});

export const JURISTILIITTO_CONNECTION_ID = 'atad-juristiliitto-oidc';

const REDIRECT_URL_STORAGE_KEY = 'original-url-before-redirect';

const domainsWithAlmaAccount = import.meta.env.VITE_SITE_URLS?.split(',') || [];

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Alma account (consumer) based auth provider
 * BEFORE COMMITTING ANY CHANGES TO PRODUCTION:
 * - Make sure to test the changes in both proxied environment (EzProxy) and normal environment
 * - Ensure IP-authentication functionality works both with and without Alma account
 */
export default function AuthProvider({
  children,
}: Readonly<AuthProviderProps>) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [authInfo, setAuthInfo] = useState<TujuAccountInformation | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [core] = useState(() => AlmaAccountCore.getInstance());
  // List of domains that should have alma account enabled

  useEffect(() => {
    const init = async () => {
      const redirectUrl = `${window.location.origin}/login/callback`;

      if (!domainsWithAlmaAccount.includes(window.location.hostname)) {
        setAuthInfo(await getAccountInformation());
      } else {
        await core.init({
          domain: import.meta.env.VITE_ALMA_TUNNUS_DOMAIN,
          clientID: import.meta.env.VITE_ALMA_TUNNUS_CLIENT_ID,
          scope: import.meta.env.VITE_ALMA_TUNNUS_SCOPE,
          realm: import.meta.env.VITE_ALMA_TUNNUS_REALM,
          audience: import.meta.env.VITE_ALMA_TUNNUS_AUDIENCE,
          redirectUri: redirectUrl,
          cacheLocation: 'localstorage',
        });
        if (window.location.pathname !== '/login/callback') {
          const tempUser = await core.autologin();

          if (tempUser) {
            setUser(tempUser);
          } else {
            setUser(await core.getUserInfo());
          }

          setAuthInfo(await getAccountInformation(await core.getAccessToken()));
        }
      }
    };

    init().then(() => setLoading(false));
  }, [core]);

  useEffect(() => {
    if (user?.sub) {
      const id = user.sub.split('|')[1];
      if (id && id.length > 0) {
        addEventToGTMDataLayer({ alma_account_user_id: id });
      }
    }
  }, [user]);

  const actions: AlmaAccountActions = useMemo(
    () => ({
      verifyEmail: async (): Promise<boolean> => {
        try {
          return await core.selfServiceVerifyEmailUrl();
        } catch (_) {
          return false;
        }
      },
      login: (connectionId?: string, redirect = true) => {
        if (redirect) {
          sessionStorage.setItem(
            REDIRECT_URL_STORAGE_KEY,
            window.location.pathname + window.location.search,
          );
        }
        const authParams: { lang: string; connection?: string } = {
          lang: 'FI',
        };
        if (connectionId) {
          authParams.connection = connectionId;
        }
        core.login({ authorizationParams: authParams });
      },
      logout: () => {
        sessionStorage.setItem(
          REDIRECT_URL_STORAGE_KEY,
          window.location.pathname + window.location.search,
        );
        core.logout(import.meta.env.VITE_ALMA_TUNNUS_LOGOUT_URL);
        setAuthInfo(undefined);
        setUser(undefined);
      },
      isAuthenticated: () => !!(authInfo?.ipAuth || authInfo?.almaIdAuth),
      getUserInfo: async () => {
        if (authInfo?.almaIdAuth) {
          const userInfo = await core.getUserInfo();
          setUser(userInfo);
          return userInfo;
        }
        return undefined;
      },
      getUserInfoWithoutCache: async () => {
        await core.getAccessToken({ cacheMode: 'off' });
        const userInfo = await core.getUserInfo();
        setUser(userInfo);
        return userInfo;
      },
      getAccessToken: async (): Promise<string | undefined> => {
        if (authInfo && !authInfo.ipRights && (await core.isAuthenticated())) {
          return core.getAccessToken();
        }
        return undefined;
      },
      handleAuthentication: async () => {
        const params = new URLSearchParams(window.location.search);
        const state = params.get('state');
        const code = params.get('code');
        if (state && code) {
          try {
            await core.handleAuthentication();
            const token = await core.getAccessToken();
            const [authInfo, userInfo] = await Promise.all([
              getAccountInformation(token),
              core.getUserInfo(),
            ]);
            setAuthInfo(authInfo);
            setUser(userInfo);
          } catch (_) {
            setUser(undefined);
            setAuthInfo(undefined);
          }
        } else {
          setUser(undefined);
          setAuthInfo(undefined);
        }
      },
    }),
    [authInfo, core],
  );

  const contextValue = useMemo(
    () => ({
      actions,
      user,
      loading,
      authInfo,
    }),
    [actions, user, loading, authInfo],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
